import { useCallback, useEffect, useMemo, useState } from 'react';
import { auditHistoryGateway } from '../index';
import type { ModuleAuditEvent } from '../index';
import { friendlyErrorMessage } from '../../../shared/friendlyError';

const SENSITIVE_METADATA_KEYS = ['password', 'token', 'ssn', 'bankaccount', 'ein', 'secret'];

/** Derived, not stored — no AuditEvent anywhere in EPN has an explicit "Result" field (see
 * docs/staging-data.md). An event type naming its own denial/failure is shown as such; everything else
 * is a completed action, so "Success" here. */
function deriveResult(eventType: string): string {
  const lower = eventType.toLowerCase();
  if (lower.includes('denied') || lower.includes('rejected') || lower.includes('failed') || lower.includes('unverified')) {
    return 'Denied';
  }
  return 'Success';
}

/** Defensive second layer — every module's own AuditEventWriter already never records these values, but
 * this keeps a stray metadata field from a future change from ever rendering here unfiltered. */
function redactMetadata(metadataJson: string | null): string {
  if (!metadataJson) return '—';
  try {
    const parsed = JSON.parse(metadataJson) as Record<string, unknown>;
    const redacted = Object.fromEntries(
      Object.entries(parsed).map(([key, value]) =>
        SENSITIVE_METADATA_KEYS.some((s) => key.toLowerCase().includes(s)) ? [key, '[redacted]'] : [key, value],
      ),
    );
    return JSON.stringify(redacted);
  } catch {
    return '[unreadable]';
  }
}

export function AuditHistoryPage() {
  const [events, setEvents] = useState<ModuleAuditEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [moduleFilter, setModuleFilter] = useState('All');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setEvents(await auditHistoryGateway.getRecentAcrossModules(100));
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not load audit history.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  const modules = useMemo(() => ['All', ...new Set(events.map((e) => e.module))], [events]);

  const filtered = useMemo(
    () =>
      events.filter((e) => {
        if (moduleFilter !== 'All' && e.module !== moduleFilter) return false;
        if (!search) return true;
        const haystack = `${e.eventType} ${e.actorEmailSnapshot ?? ''} ${e.targetType ?? ''} ${e.correlationId}`.toLowerCase();
        return haystack.includes(search.toLowerCase());
      }),
    [events, moduleFilter, search],
  );

  return (
    <section className="card card--wide">
      <div className="section-header">
        <h2>Audit History</h2>
      </div>

      {error && <p className="error">{error}</p>}

      <div className="inline-form">
        <select value={moduleFilter} onChange={(e) => setModuleFilter(e.target.value)}>
          {modules.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <input className="search-box" placeholder="Search event, actor, target, correlation id…" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      {loading && <p className="muted">Loading…</p>}
      {!loading && filtered.length === 0 && <p className="muted">No audit events found.</p>}

      {!loading && filtered.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>When</th><th>Module</th><th>Actor</th><th>Action</th><th>Entity</th><th>Result</th><th>Correlation ID</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((e) => (
              <tr key={`${e.module}-${e.id}`}>
                <td>{new Date(e.occurredAtUtc).toLocaleString()}</td>
                <td>{e.module}</td>
                <td>{e.actorEmailSnapshot ?? e.actorType}</td>
                <td>{e.eventType}</td>
                <td>{e.targetType ?? '—'}</td>
                <td>{deriveResult(e.eventType)}</td>
                <td className="muted" title={redactMetadata(e.metadataJson)}>{e.correlationId.slice(0, 8)}…</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
